import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { compose } from 'recompose';
import { Loader } from 'semantic-ui-react';

import CampaignDashboardCards from '../../components/campaign-dashboard-cards';
import CampaignDashboardTable from '../../components/campaign-dashboard-table';
import DyzioEmptyState from '../../components/dyzio-empty-state';
import TabActionBar, { ViewModes } from '../../components/tab-action-bar';

import UploadModal from '../../containers/upload-modal';
import AddAssetForm from '../../components/add-asset-form';

import hasJSONAPIClient from '../../hocs/has-jsonapi-client';
import hasRESTClient from '../../hocs/has-rest-client';
import hasLocalStorageClient from '../../hocs/has-localstorage-client';

import { authService } from '../../utils/auth-service';
import { getTrackedAssetAnalytics } from '../../utils/tracked-assets-analytics-service';

const groupByOptions = [
  { key: 'none', text: 'None', value: 'none' },
  { key: 'platform', text: 'Platform', value: 'platform' },
];

const sortByOptions = [
  { key: 'createdDateAsc', text: 'Created date (ASC)', value: 'createdDate' },
  { key: 'createdDateDesc', text: 'Created date (DESC)', value: '-createdDate' },

  { key: 'publishedDateAsc', text: 'Published date (ASC)', value: 'publishedDate' },
  { key: 'publishedDateDesc', text: 'Published date (DESC)', value: '-publishedDate' },

  { key: 'engagementsAsc', text: 'Engagements (ASC)', value: 'engagements' },
  { key: 'engagementsDesc', text: 'Engagements (DESC)', value: '-engagements' },
];

const BEGIN_LOAD_STATE = {
  isLoading: true,
  isLoaded: false,
  isFailed: false,
};

const getViewMode = campaignSettings => {
  if (!campaignSettings) return ViewModes.cardsLarge;
  const { assetsViewMode } = campaignSettings;
  return assetsViewMode || ViewModes.cardsLarge;
};

const ASSETS_PER_PAGE = 200;

const HELP_PAGE_URL = 'http://help.dyzio.co/dyzio-contextual-help/campaign-assets';

class CampaignDashboardAssets extends Component {
  constructor(props) {
    super(props);
    const campaignSettings = props.dyzioStorage.getCampaignSettings({
      campaignId: props.params.id,
      key: props.dyzioStorage.KEYS.CAMPAIGN_VIEW_MODE,
    });
    this.state = {
      trackedAssets: [],
      tableViewTrackedAssets: [],
      viewMode: getViewMode(campaignSettings),
      groupBy: 'none',
      sortBy: 'createdDate',
      uploadSupportingVisible: false,
      assetModalOpen: false,
      ...BEGIN_LOAD_STATE,
    };
  }

  componentDidMount() {
    this.requestTrackedAssets(true);
  }

  addSupportingFile = result => {
    const { selectedAsset, trackedAssets } = this.state;
    const updatedAsset = _.find(trackedAssets, { id: selectedAsset.id });
    if (!updatedAsset.localState) updatedAsset.localState = {};
    updatedAsset.localState.supportingFileAdded = true;
    this.setState({
      trackedAssets,
    });
  }

  onViewModeChanged = (event, data) => {
    const { name } = data;
    const { params: { id: campaignId }, dyzioStorage } = this.props;
    this.setState({
      viewMode: name,
    }, () => {
      const campaignSettings = dyzioStorage.getCampaignSettings({
        campaignId,
        key: dyzioStorage.KEYS.CAMPAIGN_VIEW_MODE,
      });
      const campaignUpdateOpts = {
        campaignId,
        key: dyzioStorage.KEYS.CAMPAIGN_VIEW_MODE,
        value: _.set(campaignSettings, 'assetsViewMode', name),
      };
      dyzioStorage.updateCampaignSettings(campaignUpdateOpts);
    });
  }

  onGroupByChanged = (event, data) => {
    const { value } = data;
    this.setState({
      groupBy: value,
    });
  }

  onSortByChanged = (event, data) => {
    const { value } = data;
    const { viewMode } = this.state;
    const isTableView = viewMode === ViewModes.table;
    this.setState({
      sortBy: value,
    }, () => {
      this.requestTrackedAssets(isTableView);
    });
  }

  toggleAddAssetModalState = () => {
    const { assetModalOpen } = this.state;
    this.setState({
      assetModalOpen: !assetModalOpen,
    });
  }

  toggleUploadModal = () => {
    const { uploadSupportingVisible } = this.state;
    this.setState({ uploadSupportingVisible: !uploadSupportingVisible });
  }

  addAssetHandler = details => {
    this.setState({
      isAddingNewCard: true,
    });
    const { params, jsonAPIClient } = this.props;
    const { id } = params;
    const { origin } = window.location;
    const trackedAsset = jsonAPIClient.create('trackedAssets');
    trackedAsset.set('assetId', details.assetId);
    trackedAsset.set('assetURL', details.assetURL);
    trackedAsset.set('user', {
      type: 'users',
      id: authService.uid(),
    });
    trackedAsset.set('campaign', {
      type: 'campaigns',
      id,
    });
    trackedAsset.set('platform', details.platform);
    trackedAsset.set('assetType', details.type);
    trackedAsset.set('origin', origin);
    trackedAsset.set('createdSource', 'CAMPAIGN');
    trackedAsset.sync().then(() => {
      jsonAPIClient.get('campaigns', id).then(campaign => {
        campaign.relationships('trackedAssets').add(trackedAsset);
        campaign.sync(() => {
          this.toggleAddAssetModalState();
          setTimeout(() => {
            this.requestTrackedAssets(true);
          }, 1000);
        });
      });
    });
  }

  requestTrackedAssets = (shouldQueryAssetsData = false) => {
    const { params: { id }, restClient } = this.props;
    const { sortBy } = this.state;
    this.setState(BEGIN_LOAD_STATE, () => {
      restClient.get(`trackedAssets?filter[campaign]=${id}&sort=${sortBy}&page[limit]=${ASSETS_PER_PAGE}`).then(response => {
        const { data: { data } } = response;
        this.setState({
          trackedAssets: data,
          isLoaded: true,
          isLoading: false,
          isFailed: false,
        }, () => {
          if (shouldQueryAssetsData) {
            const tableViewDataRequest = _.map(data, 'attributes');
            getTrackedAssetAnalytics(tableViewDataRequest).then(rsp => {
              const tableViewTrackedAssets = _.map(rsp, 'response.data');
              this.setState({
                tableViewTrackedAssets,
              });
            });
          }
        });
      }).catch(() => {
        this.setState({
          trackedAssets: [],
          isLoaded: true,
          isLoading: false,
          isFailed: true,
        });
      });
    });
  }

  toggleAssetModal = asset => {
    this.setState({
      selectedAsset: asset,
      uploadSupportingVisible: true,
    });
  }

  removeAssetFromList = asset => {
    const { trackedAssets } = this.state;
    const deletedAsset = _.find(trackedAssets, { id: asset.id });
    if (!deletedAsset.localState) deletedAsset.localState = {};
    deletedAsset.localState.isAssetDeleted = true;
    this.setState({
      trackedAssets,
    });
  }

  removeAssetFromServer = asset => {
    const { restClient } = this.props;
    return restClient.delete(`trackedAssets/${asset.id}`);
  }

  removeAsset = asset => {
    this.removeAssetFromServer(asset)
      .then(this.removeAssetFromList(asset))
      .catch(e => console.log(e));
  }

  getCardHamburgerOptions = () => (
    [
      {
        label: 'add data manually',
        icon: 'upload',
        value: 'upload',
        onClick: this.toggleAssetModal,
      },
      {
        label: 'remove asset',
        icon: 'delete',
        value: 'delete',
        onClick: this.removeAsset,
      },
    ]
  );

  toggleUploadModal = () => {
    const { uploadSupportingVisible } = this.state;
    this.setState({ uploadSupportingVisible: !uploadSupportingVisible });
  }

  render() {
    const {
      viewMode,
      groupBy,
      selectedAsset,
      uploadSupportingVisible,
      assetModalOpen,
      trackedAssets,
      isLoading,
      isLoaded,
      tableViewTrackedAssets,
    } = this.state;

    const isSmallCards = viewMode === ViewModes.cardsSmall;
    const isLargeCards = viewMode === ViewModes.cardsLarge;
    const isTableView = viewMode === ViewModes.table;
    const isCardsView = isSmallCards || isLargeCards;

    const noTrackedAssets = _.isEmpty(trackedAssets);

    const actionButtonProps = {
      content: 'Add an asset...',
      onClick: this.toggleAddAssetModalState,
    };

    const hamburgerOptions = this.getCardHamburgerOptions();

    return (
      <div>
        <TabActionBar
          hasActionButton
          actionButtonProps={actionButtonProps}
          isSmallCards={isSmallCards}
          isLargeCards={isLargeCards}
          isTableView={isTableView}
          onViewModeChanged={this.onViewModeChanged}
          groupByOptions={groupByOptions}
          onGroupByChanged={this.onGroupByChanged}
          sortByOptions={sortByOptions}
          onSortByChanged={this.onSortByChanged}
          helpPageUrl={HELP_PAGE_URL}
          shouldDisableButtons={noTrackedAssets}
        />

        {
          isLoading &&
          <Loader
            active
            size="big"
            content="Loading tracked assets"
          />
        }

        {
          isLoaded && noTrackedAssets &&
          <CampaignDashboardEmptyState
            buttonLabel="Add an asset..."
            content={
              <div>
                <p>There are no assets in your campaign yet.</p>
                <p>Use the button below to add your first asset.</p>
              </div>
            }
            onClick={this.toggleAddAssetModalState}
          />
        }

        {
          isLoaded && isCardsView && !noTrackedAssets &&
          <CampaignDashboardCards
            isDone={isLoaded}
            isLoading={isLoading}
            assets={trackedAssets}
            viewMode={viewMode}
            groupBy={groupBy}
            hamburgerOptions={hamburgerOptions}
          />
        }
        {
          isLoaded && isTableView && !noTrackedAssets &&
          <CampaignDashboardTable
            assets={tableViewTrackedAssets}
          />
        }

        {
          uploadSupportingVisible &&
          <UploadModal
            asset={selectedAsset}
            open={uploadSupportingVisible}
            popupToggleHandler={this.toggleUploadModal}
            successHandler={this.addSupportingFile}
          />
        }
        {
          assetModalOpen &&
          <AddAssetForm
            isOpen
            platform="external"
            popupToggleHandler={this.toggleAddAssetModalState}
            addAssetHandler={this.addAssetHandler}
          />
        }

      </div>
    );
  }
}

CampaignDashboardAssets.propTypes = {
  jsonAPIClient: PropTypes.object.isRequired,
  restClient: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired,
  dyzioStorage: PropTypes.object.isRequired,
};

export default compose(
  hasJSONAPIClient,
  hasRESTClient,
  hasLocalStorageClient,
)(CampaignDashboardAssets);



/** WEBPACK FOOTER **
 ** ./src/containers/campaign-dashboard-assets/index.js
 **/